import * as React from 'react';
import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Badge, Card, CardContent, Skeleton } from '~/components/ui/primitives';
import { ApiError } from '~/lib/api';
import { cn } from '~/lib/utils';
import { INTERVENTION_STATUS_LABELS, type InterventionStatus } from '@hch/shared';

/**
 * Etats transverses : chargement, erreur, vide.
 *
 * Chaque vue de l'application doit gerer ces trois cas. Les factoriser ici garantit
 * qu'ils sont traites partout de la meme facon, et surtout qu'ils ne sont pas
 * oublies — une liste vide qui n'affiche rien du tout laisse l'utilisateur devant
 * un ecran blanc sans savoir si l'application a echoue ou s'il n'y a rien a voir.
 */

export function LoadingState({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-3', className)} role="status" aria-live="polite">
      <span className="sr-only">Chargement en cours…</span>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-20 w-full" />
      ))}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  // Une erreur metier porte un message redige pour l'utilisateur : on l'affiche tel
  // quel. Une erreur technique est remplacee par un message neutre.
  const message =
    error instanceof ApiError
      ? error.message
      : 'Une erreur inattendue est survenue. Merci de reessayer.';

  return (
    <Card className={cn('border-destructive/40', className)}>
      <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
        <div className="rounded-full bg-destructive/10 p-3">
          <AlertTriangle className="size-5 text-destructive" aria-hidden />
        </div>
        <div className="space-y-1">
          <p className="font-medium">Impossible d'afficher ces donnees</p>
          <p role="alert" className="text-sm text-muted-foreground max-w-md">
            {message}
          </p>
        </div>
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RefreshCw aria-hidden />
            Reessayer
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn('border-dashed', className)}>
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <div className="rounded-full bg-muted p-3">
          <Icon className="size-5 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          <p className="font-medium">{title}</p>
          {description ? (
            <p className="text-sm text-muted-foreground max-w-md">{description}</p>
          ) : null}
        </div>
        {action}
      </CardContent>
    </Card>
  );
}

/**
 * Etiquette de statut d'intervention.
 *
 * La couleur ne porte JAMAIS l'information a elle seule : le libelle textuel est
 * toujours present (RGAA 3.1 — l'information ne doit pas dependre de la couleur).
 */
const STATUS_VARIANT: Record<
  InterventionStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline'
> = {
  DRAFT: 'outline',
  BOOKED: 'secondary',
  CONFIRMED: 'default',
  IN_PROGRESS: 'warning',
  DONE: 'success',
  PAID: 'success',
  CANCELLED: 'destructive',
  NO_SHOW: 'destructive',
};

export function StatusBadge({
  status,
  className,
}: {
  status: InterventionStatus;
  className?: string;
}) {
  return (
    <Badge variant={STATUS_VARIANT[status] ?? 'outline'} className={className}>
      {INTERVENTION_STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

// --- Entete de page -----------------------------------------------------------

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: React.ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="space-y-1">
        <h1 className="app-page-title">{title}</h1>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </header>
  );
}

// --- Indicateur cle -------------------------------------------------------------

export function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon?: React.ComponentType<{ className?: string }>;
  tone?: 'default' | 'success' | 'warning' | 'destructive';
}) {
  const toneClass = {
    default: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    destructive: 'text-destructive',
  }[tone];

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex items-start justify-between gap-3">
        <div className="space-y-1.5 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground truncate">
            {label}
          </p>
          <p className={cn('app-kpi-value', toneClass)}>{value}</p>
          {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        </div>
        {Icon ? (
          <div className="rounded-md bg-muted p-2 shrink-0">
            <Icon className="size-4 text-muted-foreground" />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
